import * as Sentry from '@sentry/browser';

import parseSectionJson from '../utils/parseSectionJson';

class ProductForm extends HTMLElement {
  constructor() {
    super();

    this.checkoutRedirect = this.getAttribute('data-checkout-redirect') === 'true';

    // Setup elements
    this.form = this.querySelector('form');
    this.button = this.querySelector('[data-atc-button]');
    this.variantField = this.querySelector('[data-variant-field]');
    this.variants = JSON.parse(this.querySelector('[data-variants]').innerHTML);
    this.errorEl = this.querySelector('[data-error]');
    this.simPhysicalInfo = this.querySelector('[data-sim-physical-info]');
    this.esimInfo = this.querySelector('[data-esim-info]');
    this.numberNewInfo = this.querySelector('[data-number-new-info]');
    this.numberKeepInfo = this.querySelector('[data-number-keep-info]');
    this.offer = this.querySelector('[data-offer]')?.value;
    this.offerLength = this.querySelector('[data-offer-length]')?.value;
    this.offerOriginalSubtotal = this.querySelector('[data-offer-original-subtotal]')?.value;
    this.renewalPrice = this.querySelector('[data-renewal-price]');
    this.renewalOptions = JSON.parse(this.querySelector('[data-renewals]').innerHTML);
    this.renewalInfo = this.querySelector('[data-renewal-info]');
    this.renewalPlans = JSON.parse(this.querySelector('[data-renewal-plans]')?.innerHTML || '[]');
    this.portinRestrictedEl = this.querySelector('[data-portin-restricted]');
    this.newNumberPlanEl = this.querySelector('[data-new-number-plan]');
    this.planOptions = JSON.parse(this.querySelector('[data-plan-options]')?.innerHTML || '[]');

    if (this.planOptions.length > 0) {
      this.selectedVariant = this.planOptions.find((variant) => variant.id === this.variantField.value);
    } else {
      this.selectedVariant = this.variants.find((variant) => variant.id === this.variantField.value);
    }

    // Bind event handlers
    this.form.addEventListener('submit', this.handleSubmit.bind(this));
    this.form.addEventListener('change', this.handleChange.bind(this));
  }

  updateSimInfo() {
    const isEsim = this.selectedVariant ? this.selectedVariant.simType === 'esim' : true;
    this.simPhysicalInfo.classList.toggle('hidden', isEsim);
    this.esimInfo.classList.toggle('hidden', !isEsim);
  }

  updateNumberInfo(optionNumber) {
    const isNumberNew = optionNumber === 'new';
    this.numberNewInfo.classList.toggle('hidden', !isNumberNew);
    this.numberKeepInfo.classList.toggle('hidden', isNumberNew);
  }

  updateRenewalPrice(renewal) {
    if (!this.renewalPrice || !this.renewalOptions || !renewal || !this.renewalInfo) {
      return;
    }

    const renewalOption = this.renewalOptions.find((option) => option.value === renewal);
    this.renewalPrice.textContent =
      renewalOption.months > 1
        ? `${renewalOption.priceMonthlyFormatted}/mo (${renewalOption.priceFormatted} upfront)`
        : `${renewalOption.priceMonthlyFormatted}/mo`;

    const { billingMonths } = this.selectedVariant;
    const renewalInfoContent = this.renewalInfo.dataset.content;
    this.renewalInfo.textContent = renewalInfoContent.replace(
      '{{billing_months}}',
      billingMonths > 1 ? `${billingMonths} months` : 'the first month',
    );
  }

  updateRenewalPlanPrice(renewalPlanId) {
    if (!this.renewalPrice || !this.renewalPlans || !renewalPlanId) {
      return;
    }

    const renewalPlan = this.renewalPlans.find((option) => option.id === renewalPlanId);
    this.renewalPrice.textContent = `${renewalPlan.priceFormatted}/mo`;
  }

  handleChange() {
    const formData = new FormData(this.form);
    const { optionNumber, option1, option2, option3, renewal, renewalPlan, planOption } = Object.fromEntries(formData.entries());

    if (planOption) {
      this.selectedVariant = this.planOptions.find((plan) => (
        plan.productId === planOption && (plan.option1 === option1 || !option1) && plan.option2 === option2 && plan.option3 === option3
      ));
    } else {
      this.selectedVariant = this.variants.find((variant) => {
        return Object.entries({ option1, option2, option3 }).every(([key, value]) => {
          return variant[key] === value;
        });
      });
    }

    this.updateNumberInfo(optionNumber);
    this.updateSimInfo();

    if (renewal) {
      this.updateRenewalPrice(renewal);
    }

    if (renewalPlan) {
      this.updateRenewalPlanPrice(renewalPlan);
    }

    document.body.dispatchEvent(
      new CustomEvent('variant:change', {
        detail: {
          variant: this.selectedVariant,
        },
      }),
    );

    this.variantField.value = this.selectedVariant?.id;
  }

  async handleSubmit(event) {
    event.preventDefault();

    // Update the form state
    this.button.disabled = true;
    this.button.classList.add('loading');
    this.errorEl.classList.add('hidden');

    const properties = {};
    if (this.offer) {
      properties['_offer'] = this.offer;
    }

    if (this.selectedVariant.offer) {
      properties['_offer'] = this.selectedVariant.offer;
    }

    if (this.selectedVariant.offer === 'first_month_offer') {
      properties['_offer_length'] = 30;
      properties['_renewal_price'] = this.selectedVariant.offerOriginalSubtotal;
      properties['_renewal_period'] = 30;
    }

    if (this.offerLength) {
      properties['_offer_length'] = this.offerLength;
    }

    if (this.offerOriginalSubtotal) {
      properties['_offer_original_subtotal'] = this.offerOriginalSubtotal;
    }

    if (this.selectedVariant.offerOriginalSubtotal) {
      properties['_offer_original_subtotal'] = this.selectedVariant.offerOriginalSubtotal;
    }

    if (this.newNumberPlanEl?.value) {
      properties['_offer_new_number_plan'] = this.newNumberPlanEl?.value;
    }

    properties['_offer_portin_restricted'] = String(this.portinRestrictedEl?.value === 'true');

    // Add selected properties to the cart
    const formData = new FormData(this.form);
    const { optionNumber, renewal, renewalPlan, campaign, planOption } = Object.fromEntries(formData.entries());
    properties['_number'] = optionNumber;

    if (campaign) {
      properties['_campaign'] = campaign;
    }

    properties['_compare_price'] = this.selectedVariant.comparePriceValue;
    properties['_price'] = this.selectedVariant.pricePerMonthValue;

    if (renewal) {
      const renewalOption = this.renewalOptions.find((option) => option.value === renewal);
      if (renewalOption) {
        properties['_renewal_period'] = renewal;
        properties['_renewal_price'] = renewalOption.price;
        properties['_renewal_price_monthly'] = renewalOption.priceMonthly;
        properties['_renewal_months'] = renewalOption.months;
      }
    }

    if (renewalPlan) {
      const renewalPlanProduct = this.renewalPlans.find((option) => option.id === renewalPlan);
      const renewalPlanId = renewalPlanProduct.sku.split('_')[1];
      properties['_renewal_period'] = '30';
      properties['_renewal_price'] = renewalPlanProduct.price;
      properties['_renewal_price_monthly'] = renewalPlanProduct.price;
      properties['_renewal_months'] = 1;
      properties['_renewal_plan'] = renewalPlanId;
    }

    try {
      const data = {
        items: [
          {
            id: this.selectedVariant.id,
            selling_plan: this.selectedVariant.sellingPlan,
            quantity: 1,
            properties,
          },
        ],
        sections: ['cart', 'cart-json'],
      };

      // Limits the number of plans to 1
      const clearResponse = await fetch(`${window.Shopify.routes.root}cart/clear.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
      });

      if (!clearResponse.ok) {
        const responseData = await response.json();
        throw new Error('Clear Cart Failed | ' + JSON.stringify(responseData));
      }

      const response = await fetch(`${window.Shopify.routes.root}cart/add.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      });

      if (!response.ok) {
        const responseData = await response.json();
        throw new Error('Add To Cart Failed | ' + JSON.stringify(responseData));
      }

      if (this.checkoutRedirect) {
        window.location = '/checkout';
        return;
      } else {
        const responseData = await response.json();
        this.handleCartResponse(responseData);
      }
    } catch (error) {
      this.handleError(error);
    }

    // Reset the form state
    this.button.disabled = false;
    this.button.classList.remove('loading');
  }

  handleCartResponse(data) {
    document.body.dispatchEvent(
      new CustomEvent('cart:rebuild', {
        detail: {
          html: data.sections.cart,
        },
      }),
    );

    document.body.dispatchEvent(
      new CustomEvent('cart:updated', {
        detail: {
          cart: parseSectionJson(data.sections['cart-json']).items,
          shouldOpenCart: true,
        },
      }),
    );
  }

  handleError(error) {
    Sentry.captureException(error);
    console.error('Error:', error);
    this.errorEl.classList.remove('hidden');
  }
}

customElements.define('product-form', ProductForm);
